import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from '../components/Sidebar';
import DashboardPage from '../pages/DashboardPage';
import InvoiceSearchPage from '../pages/InvoiceSearchPage';
import VendorSearchPage from '../pages/VendorSearchPage';
import ApprovalPage from '../pages/ApprovalPage';
import ApprovalDetailPage from '../pages/ApprovalDetailPage';
import PaymentPage from '../pages/PaymentPage';
import PaymentBatchesPage from '../pages/PaymentBatchesPage';
import TdsPage from '../pages/TdsPage';
import TdsBatchesPage from '../pages/TdsBatchesPage';
import TdsPaidBatchesPage from '../pages/TdsPaidBatchesPage';
import TdsPaidBatchInvoicesPage from '../pages/TdsPaidBatchInvoicesPage';
import TdsPendingBatchInvoicesPage from '../pages/TdsPendingBatchInvoicesPage';
import InvoiceDetailPage from '../pages/InvoiceDetailPage';
import InvoiceExcelViewPage from '../pages/InvoiceExcelViewPage';
import AnnualAuditPage from '../pages/AnnualAuditPage';

import api from '../api';

const ProtectedLayout = () => {
    const [page, setPage] = useState('dashboard');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedApproval, setSelectedApproval] = useState(null);
    const [selectedTdsPaidBatch, setSelectedTdsPaidBatch] = useState(null);
    const [selectedTdsPendingBatch, setSelectedTdsPendingBatch] = useState(null);

    // Handle approval action (approve/reject)
    // Handle approval action (approve/reject)
    const handleApprovalAction = useCallback(async (id, action) => {
        const isApprove = action === 'APPROVE';
        const actionText = isApprove ? 'approve' : 'reject';

        const confirmAction = () => {
            return new Promise((resolve) => {
                const toastId = toast(
                    <div className="flex flex-col space-y-4 p-2">
                        <p className="text-gray-800">
                            Are you sure you want to <strong>{actionText.toUpperCase()}</strong> this invoice?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md border border-gray-200 transition-colors"
                                onClick={() => { toast.dismiss(toastId); resolve(false); }}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-3 py-1.5 text-sm text-white rounded-md shadow-sm transition-colors ${isApprove ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                onClick={() => { toast.dismiss(toastId); resolve(true); }}
                            >
                                {isApprove ? 'Approve' : 'Reject'}
                            </button>
                        </div>
                    </div>,
                    {
                        position: 'top-center',
                        autoClose: false,
                        closeOnClick: false,
                        draggable: false,
                        closeButton: false,
                        style: { minWidth: '350px', cursor: 'default' }
                    }
                );
            });
        };

        const confirmed = await confirmAction();
        if (!confirmed) {
            return false;
        }

        try {
            const endpoint = isApprove
                ? `/invoices/${id}/status/approve`
                : `/invoices/${id}/status/reject`;

            const { data: updatedApproval } = await api.put(endpoint);

            // Update the selected approval with new status
            setSelectedApproval(prev => ({
                ...prev,
                status: updatedApproval.status || (isApprove ? 'APPROVED' : 'REJECTED')
            }));

            // Show success message
            toast.success(`Invoice ${actionText}d successfully!`);
            return true;

        } catch (error) {
            console.error(`Error ${actionText}ing invoice:`, error);
            const errorMessage = error.response?.data?.message || error.message;

            if (error.response?.status === 403) {
                toast.error('Access denied: You do not have permission to perform this action');
            } else {
                toast.error(`Failed to ${actionText} invoice: ${errorMessage}`);
            }
            return false;
        }
    }, []);

    const renderPage = () => {
        switch (page) {
            case 'dashboard': return <DashboardPage navigate={setPage} />;
            case 'invoices': return (
                <InvoiceSearchPage
                    navigate={setPage}
                    setSelectedInvoice={setSelectedInvoice}
                />
            );
            case 'invoice-detail': return (
                <InvoiceDetailPage
                    invoice={selectedInvoice}
                    onBack={() => setPage('invoices')}
                />
            );
            case 'invoice-excel-view': return <InvoiceExcelViewPage />;
            case 'annual-audit': return <AnnualAuditPage />;
            case 'vendors': return <VendorSearchPage />;
            case 'approvals': return (
                <ApprovalPage
                    navigate={(page) => {
                        if (page === 'approval-detail') {
                            setPage('approval-detail');
                        } else {
                            setPage(page);
                        }
                    }}
                    setSelectedInvoice={setSelectedApproval}
                />
            );
            case 'approval-detail': return (
                <ApprovalDetailPage
                    approval={selectedApproval}
                    onBack={() => setPage('approvals')}
                    onApprove={(id) => handleApprovalAction(id, 'APPROVE')}
                    onReject={(id) => handleApprovalAction(id, 'REJECT')}
                />
            );
            case 'payments': return <PaymentPage />;
            case 'payment-batches': return <PaymentBatchesPage navigate={setPage} />;
            case 'tds-invoices': return <TdsPage />;
            case 'tds': return <TdsPage />;
            case 'tds-batches': return (
                <TdsBatchesPage
                    navigate={setPage}
                    setSelectedPendingBatch={setSelectedTdsPendingBatch}
                />
            );
            case 'tds-batches-paid': return <TdsPaidBatchesPage navigate={setPage} setSelectedBatch={setSelectedTdsPaidBatch} />;
            case 'tds-paid-batch-invoices': return (
                <TdsPaidBatchInvoicesPage
                    batch={selectedTdsPaidBatch}
                    onBack={() => setPage('tds-batches-paid')}
                />
            );
            case 'tds-pending-batch-invoices': return (
                <TdsPendingBatchInvoicesPage
                    batch={selectedTdsPendingBatch}
                    onBack={() => setPage('tds-batches')}
                />
            );
            default: return <DashboardPage navigate={setPage} />;
        }
    };

    return (
        <div className="flex h-screen bg-neutral-50 overflow-hidden">
            {/* Sidebar container */}
            <div className="flex-shrink-0 h-full lg:relative">
                <Sidebar navigate={setPage} currentPage={page} />
            </div>

            {/* Main content area with scrolling */}
            <div className="flex-1 flex flex-col min-w-0 lg:ml-0">
                <main className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {renderPage()}
                </main>
            </div>
        </div>
    );
};

export default ProtectedLayout;